/*
  Starts sliders that are far below the fold only when the visitor scrolls near them.

  Every Swiper on this theme is created inside $(document).ready and nothing keeps the
  returned instance, so wrapping the constructor is safe. Creating a Swiper measures the
  page layout; on the ~6,000-element product page doing that for sliders nobody can see yet
  cost seconds of main-thread time on a mid-range phone. Sliders on or near the first screen
  (and hidden ones, which measure as 0) are created immediately, exactly as before.

  A waiting slider gets its slides sized from its own options (slidesPerView, spaceBetween,
  breakpoints) and room kept for its pagination dots, so it already has its final height;
  otherwise every slide is full width until Swiper starts and the page would jump. Swiper
  overwrites the slide styles on start, and the pagination styles are handed back to it.

  Must load right after swiper.js (both deferred) so it runs before $(document).ready.
*/
(function () {
  var RealSwiper = window.Swiper;
  if (typeof RealSwiper !== 'function' || !('IntersectionObserver' in window)) return;

  var anchoring = window.CSS && CSS.supports && CSS.supports('overflow-anchor', 'auto');

  function firstElement(target) {
    if (typeof target === 'string') return document.querySelector(target);
    if (target && target.nodeType === 1) return target;
    return null;
  }

  function currentOptions(params) {
    var options = { slidesPerView: params.slidesPerView || 1, spaceBetween: params.spaceBetween || 0 };
    var breakpoints = params.breakpoints || {};
    Object.keys(breakpoints)
      .map(Number)
      .filter(function (width) { return !isNaN(width) && width <= window.innerWidth; })
      .sort(function (a, b) { return a - b; })
      .forEach(function (width) {
        var bp = breakpoints[width];
        if (bp.slidesPerView !== undefined) options.slidesPerView = bp.slidesPerView;
        if (bp.spaceBetween !== undefined) options.spaceBetween = bp.spaceBetween;
      });
    return options;
  }

  function sizeSlides(target, params) {
    if (params.direction === 'vertical') return [];
    var options = currentOptions(params);
    var perView = Number(options.slidesPerView);
    var gap = Number(options.spaceBetween) || 0;
    if (!(perView > 0)) return [];

    var reserved = [];
    var sliders = typeof target === 'string' ? document.querySelectorAll(target) : [target];
    for (var s = 0; s < sliders.length; s++) {
      var slides = sliders[s].querySelectorAll(':scope > .swiper-wrapper > .swiper-slide');
      for (var i = 0; i < slides.length; i++) {
        slides[i].style.width = 'calc((100% - ' + gap * (perView - 1) + 'px) / ' + perView + ')';
        slides[i].style.marginRight = gap + 'px';
      }

      // The dots only exist once Swiper runs (and none are shown when every slide fits).
      var dots = paginationElement(sliders[s], params);
      if (dots && slides.length > perView && getComputedStyle(dots).display === 'none') {
        var styles = getComputedStyle(sliders[s]);
        dots.style.display = 'block';
        dots.style.height =
          styles.getPropertyValue('--swiper-pagination-bullet-height').trim() ||
          styles.getPropertyValue('--swiper-pagination-bullet-size').trim() ||
          '8px';
        reserved.push(dots);
      }
    }
    return reserved;
  }

  function paginationElement(slider, params) {
    var selector = params.pagination && params.pagination.el;
    if (typeof selector !== 'string') return null;
    try {
      return slider.querySelector(selector);
    } catch (error) {
      return null;
    }
  }

  function LazySwiper(target, params) {
    params = params || {};
    var el = firstElement(target);
    var rect = el ? el.getBoundingClientRect() : null;
    var farBelow = rect && rect.height > 0 && rect.top > window.innerHeight * 1.5;

    if (!farBelow) {
      return new RealSwiper(target, params);
    }

    var reserved = sizeSlides(target, params) || [];

    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          observer.disconnect();
          var before = el.getBoundingClientRect();
          reserved.forEach(function (dots) {
            dots.style.display = '';
            dots.style.height = '';
          });
          new RealSwiper(target, params);
          // Scrolling back up past a slider that was skipped (e.g. after jumping to reviews):
          // it starts above the screen, and any small change in its height moves the view. Browsers
          // with scroll anchoring keep the view steady themselves; for the rest, do it here.
          if (before.bottom <= 0 && !anchoring) {
            window.scrollBy(0, el.getBoundingClientRect().height - before.height);
          }
          return;
        }
      }
    }, { rootMargin: '600px 0px' });
    observer.observe(el);
  }

  LazySwiper.prototype = RealSwiper.prototype;
  Object.keys(RealSwiper).forEach(function (key) {
    LazySwiper[key] = RealSwiper[key];
  });
  LazySwiper.use = RealSwiper.use ? RealSwiper.use.bind(RealSwiper) : undefined;
  window.Swiper = LazySwiper;
})();
